import React, {useState, useEffect} from "react";
import {Form, Button, Table, Col, Row, Modal} from "react-bootstrap";
import {useNavigate} from "react-router-dom";
import '../../App.css';
// import {addCustomer, updateCustomer, deleteCustomer, updateCustomerBalance, createTransaction, fetchAccounts} from "../../api/api";
import {addCustomer, updateCustomer, deleteCustomer} from "../../api/api";
import {useCustomers} from "../../CustomerContext";
import {useAccounts} from "../../contextjs/AccountContext";
import {useTransactions} from "../../contextjs/TransactionContext";
import { useUser } from "../../UserContext";
import { hasPermission } from "../utils/permissions";
import { apiInstance } from "../../api/api";
import { fetchStoreAccountSettings } from "../../api/api";

const Customer = () => {
    // states from CustomerContext.js
    const { customers, fetchCustomers } = useCustomers();
    const { loadTransactions } = useTransactions();
    const { fetchAccountsData } = useAccounts();
    const [showModal, setShowModal] = useState(false);
    const [newCustomer, setNewCustomer] = useState({
        customer_name: '',
        location: '',
        phone_number: '',
        customer_balance_limit: 0
    });

    const navigate = useNavigate();
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState({}); // state to store the selected customer for editing
    const [searchTerm, setSearchTerm] = useState(''); // search term for customers
    // state for payment amount and selected customer for payment
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [paymentSettings, setPaymentSettings] = useState([])
    const [paymentCustomer, setPaymentCustomer] = useState(null);
    const { user } = useUser();
    // filter balances
    const [filterBalance, setFilterBalance] = useState(false);
    const [filterExcessPay, setFilterExcessPay] = useState(false);

    const handleBack = () => {
        navigate(-1);
    };

    // fetchCustomers from CustomerContext.js
    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    useEffect(() => {
        fetchStoreAccountSettings({ usage_type: 'PAYMENT'})
            .then((settings) => setPaymentSettings(settings.filter((setting) => setting.is_enabled)))
            .catch((error) => console.error('Unable to load customer payment accounts', error));
    }, []);


    const handleShow = () => setShowModal(true);
    const handleClose = () => setShowModal(false);

    const handleChange = (e) => {
        setNewCustomer({...newCustomer, [e.target.name]: e.target.value});
    };

    const handleShowEditModal = (customer) => {
        setSelectedCustomer(customer);
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => setShowEditModal(false);

    const handleEditChange = (e) => {
        const {name, value} = e.target;
        setSelectedCustomer({...selectedCustomer, [name]: value});
    };

    const handleUpdateCustomer = async (e) => {
        e.preventDefault();
        const updatedCustomer = await updateCustomer(selectedCustomer); // updates customer in the backend
        // const updatedCustomers = customers.map(customer => customer.id === updatedCustomer.id ? updatedCustomer : customer);
        // setCustomers(updatedCustomers);
        console.log('Updated customer:', updatedCustomer);
        fetchCustomers(); // re-fetch customers to update global state
        handleCloseEditModal();
    };


    const handleDeleteCustomer = async (customerId) => {
        const isConfirmed = window.confirm('Are you sure you want to delete this customer?');
        if(isConfirmed) {
            await deleteCustomer(customerId); // delete customer in the backend
            fetchCustomers(); // re-fetch customers to update global state
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await addCustomer(newCustomer);
            setNewCustomer({
                customer_name: '',
                location: '',
                phone_number: '',
                customer_balance: 0,
                customer_balance_limit: 0
            });
            handleClose();
        } catch (error) {
            console.log('Error adding customer:', error);
        }
        fetchCustomers(); // re-fetch customers to update global state
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // handler for filtering customers with balances
    const handleFilterBalanceChange = (e) => {
        setFilterBalance(e.target.checked);
        if (e.target.checked) setFilterExcessPay(false); // uncheck excess pay filter if balance filter is checked
    };

    const handleFilterExcessPayChange = (e) => {
        setFilterExcessPay(e.target.checked);
        if (e.target.checked) setFilterBalance(false); // uncheck balance filter if excess pay filter is checked
    };

    const filteredCustomers = customers.filter(customer => {
        const matchesSearch = customer.customer_name.toLowerCase().includes(searchTerm.toLowerCase());
        const bal = Number(customer.customer_balance ?? 0);
        const matchesBalanceFilter = !filterBalance || (Number.isFinite(bal) && bal > 0);
        const matchesExcessPayFilter = !filterExcessPay || (Number.isFinite(bal) && bal < 0);
        return matchesSearch && matchesBalanceFilter && matchesExcessPayFilter;
    });

    const handleReceivePayment = async (customer) => {
      if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
        alert('Please enter a valid payment amount');
        return;
      }
      if (!paymentMethod) {
        alert('Please select a payment method');
        return;
      }

      try {
          const selectedSetting = paymentSettings.find(
              (setting) => String(setting.account) === String(paymentMethod)
          );
          const legacyMethod = paymentMethod.startsWith('legacy:')
              ? paymentMethod.replace('legacy:', '')
              : null;
          const response = await apiInstance.post(`/customers/${customer.id}/receive-payment/`, {
              payment_method: selectedSetting?.payment_channel?.toLowerCase() || legacyMethod || 'other',
              payment_account: selectedSetting?.account,
              payment_amount: paymentAmount
          });
            console.log('Payment successful:', response.data);

            fetchCustomers(); // re-fetch customers to update global state
            setPaymentAmount('');
            setPaymentMethod('');
            setPaymentCustomer(null);

            loadTransactions(); // re-fetch transactions to update the global state
            fetchAccountsData(); // re-fetch accounts to update global state
      } catch (error) {
          console.error('Error receiving payment:', error);
          alert(error?.response?.data?.error || 'Failed to receive customer payment.');
      }
    };

    // define permission-checking functions
    const canAddCustomer = () => {
        return hasPermission(user, 'BeeqasiApp.add_customer');
        // user.is_superuser || user.groups.includes("Admin")
    };

    const canEditCustomer = () => {
        return hasPermission(user, 'BeeqasiApp.change_customer');
        // user.is_superuser || user.groups.includes("Admin")
    };

    const canDeleteCustomer = () => {
        return hasPermission(user, 'BeeqasiApp.delete_customer');
    };

    return (
        <div>
            <div className="back-button-container">
                <Button variant="secondary" onClick={handleBack}>Back</Button>
            </div>
            <h1>Customers</h1>
            {canAddCustomer() && (
                <Button variant="primary" onClick={handleShow} className="mb-3">
                    Add Customer</Button>
            )}

            {/* Search input */}
            <Row className="mb-3 justify-content-center">
                <Col md={6}>
                    <Form.Control
                        type="text"
                        placeholder="Search customer..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        />
                </Col>
                <Col md="auto">
                    <Form.Check
                        type="checkbox"
                        label="Show Balances > 0"
                        checked={filterBalance}
                        onChange={handleFilterBalanceChange}
                    />
                </Col>
                <Col md="auto">
                    <Form.Check
                        type="checkbox"
                        label="Show Excess Pay < 0"
                        checked={filterExcessPay}
                        onChange={handleFilterExcessPayChange}
                    />
                </Col>
            </Row>

            <Table striped bordered hover>
                <thead>
                <tr>
                    <th>Name</th>
                    <th>Location</th>
                    <th>Phone Number</th>
                    <th>Balance</th>
                    <th>Customer Limit</th>
                    <th>Actions</th>
                </tr>
                </thead>
                <tbody>
                {filteredCustomers.map((customer, index) => (
                    <tr key={index}>
                        <td style={{ color: customer.customer_balance > 0 ? "red": "inherit"}}>
                            <button className="btn btn-link p-0" onClick={() => handleShowEditModal(customer)}
                                    style={{ textDecoration: 'none'}}>
                            {customer.customer_name}
                            </button>
                        </td>
                        <td>{customer.location}</td>
                        <td>{customer.phone_number}</td>
                        {/*<td>{customer.customer_balance}</td>*/}
                        <td>{Number(customer.customer_balance ?? 0).toLocaleString()}</td>
                        <td>{customer.customer_balance_limit}</td>
                        <td>
                            {canEditCustomer() && (
                                <Button variant="success" onClick={() => setPaymentCustomer(customer)} className="button-spacing">Receive Payment</Button>
                            )}
                            {canEditCustomer() && (
                                <Button variant="info" onClick={() => handleShowEditModal(customer)} className="button-spacing">Edit</Button>
                            )}
                            {canDeleteCustomer() && (
                                <Button variant="danger" onClick={() => handleDeleteCustomer(customer.id)}>Delete</Button>
                            )}

                        </td>
                    </tr>
                ))}
                </tbody>
            </Table>

            {canAddCustomer() && (
                <Modal show={showModal} onHide={handleClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>Add Customer</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form onSubmit={handleSubmit}>
                            <Row className="mb-3">
                                <Form.Group>
                                    <Form.Label column sm="4">Name</Form.Label>
                                    <Col sm="8">
                                        <Form.Control
                                            type="text"
                                            name="customer_name"
                                            value={newCustomer.customer_name}
                                            onChange={handleChange} required/>
                                    </Col>
                                </Form.Group>
                            </Row>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Location</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="text"
                                        name="location"
                                        value={newCustomer.location}
                                        onChange={handleChange}/>
                                </Col>
                            </Form.Group>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Phone Number</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="text"
                                        name="phone_number"
                                        value={newCustomer.phone_number}
                                        onChange={handleChange}/>
                                </Col>
                            </Form.Group>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Balance</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="number"
                                        name="customer_balance"
                                        value={newCustomer.customer_balance}
                                        onChange={handleChange}/>
                                </Col>
                            </Form.Group>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Customer Limit</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="number"
                                        name="customer_balance_limit"
                                        value={newCustomer.customer_balance_limit}
                                        onChange={handleChange}/>
                                    </Col>
                            </Form.Group>

                            <Button variant="success" type="submit">Add Customer</Button>
                        </Form>
                    </Modal.Body>
                </Modal>
            )}

            {/* Edit Customer Modal */}
            {canEditCustomer() && (
                <Modal show={showEditModal} onHide={handleCloseEditModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Customer</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form onSubmit={handleUpdateCustomer}>
                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Name</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="text"
                                        name="customer_name"
                                        value={selectedCustomer.customer_name}
                                        onChange={handleEditChange}/>
                                </Col>
                            </Form.Group>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Location</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="text"
                                        name="location"
                                        value={selectedCustomer.location}
                                        onChange={handleEditChange}/>
                                </Col>
                            </Form.Group>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Phone Number</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="number"
                                        name="phone_number"
                                        value={selectedCustomer.phone_number}
                                        onChange={handleEditChange}/>
                                </Col>
                            </Form.Group>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Balance</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="number"
                                        name="customer_balance"
                                        value={selectedCustomer.customer_balance}
                                        // onChange={handleEditChange}/>
                                        readOnly
                                        plaintext
                                        />
                                </Col>
                            </Form.Group>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Customer Limit</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="number"
                                        name="customer_balance_limit"
                                        value={selectedCustomer.customer_balance_limit}
                                        onChange={handleEditChange}/>
                                </Col>
                            </Form.Group>

                            <Button variant="success" type="submit">Update Customer</Button>
                        </Form>
                    </Modal.Body>
                </Modal>
            )}

            {/*modal for receiving payment*/}
            <Modal show={!!paymentCustomer} onHide={() => setPaymentCustomer(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Receive Payment from {paymentCustomer?.customer_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group as="div" className="mb-3">
                            <Form.Label>Payment Amount</Form.Label>
                            <Form.Control
                                type="number"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(e.target.value)}
                                placeholder="Enter amount received"
                            />
                        </Form.Group>
                        <Form.Group as="div" className="mb-3">
                            <Form.Label>Payment Method</Form.Label>
                            <Form.Control
                                as="select"
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                            >
                                <option value="">Select Payment Method</option>
                                {paymentSettings.map((setting) => (
                                    <option key={setting.id} value={setting.account}>
                                        {setting.label_override || setting.account_name}
                                    </option>
                                ))}
                                {paymentSettings.length === 0 && (
                                    <>
                                        <option value="legacy:cash">Cash</option>
                                        <option value="legacy:mpesa">Mpesa Till</option>
                                        <option value="legacy:kcb">KCB</option>
                                        <option value="legacy:equity">Equity</option>
                                        <option value="legacy:george-mpesa">George Mpesa</option>
                                    </>
                                )}
                            </Form.Control>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setPaymentCustomer(null)}>Close</Button>
                    <Button variant="success" onClick={() => handleReceivePayment(paymentCustomer)}>Receive Payment</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Customer;
